import { Navigate, Route, Routes } from 'react-router-dom';

import { settingsChatbotsRoutes } from 'app/views/layout/views/settings/views/chatbots';
import { SurveyCustomizationLinkView } from 'app/views/layout/views/settings/views/satisfaction/survey/components/customization/view/link/surveyCustomizationLink.view';
import { SurveyCustomizationTextToSpeechView } from 'app/views/layout/views/settings/views/satisfaction/survey/components/customization/view/textToSpeech/surveyCustomizationTextToSpeech.view';
import { NotFound } from 'components/404/NotFound';
import { Satisfaction } from 'components/settings/components/sections/satisfaction/Satisfaction';
import SatisfactionChatCustomization from 'components/settings/containers/sections/satisfaction/chatCustomization';
import SatisfactionChatPanel from 'components/settings/containers/sections/satisfaction/chatPanel';
import { DixaMessengerCustomizationPageRoute } from 'components/settings/containers/sections/satisfaction/dixaMessengerCustomizations';
import { SatisfactionDixaMessengerPanel } from 'components/settings/containers/sections/satisfaction/dixaMessengerPanel';
import SatisfactionEmailCustomization from 'components/settings/containers/sections/satisfaction/emailCustomization';
import SatisfactionEmailPanel from 'components/settings/containers/sections/satisfaction/emailPanel';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { KnowledgeHelpCenterRoutes } from '../knowledge/knowledgeHelpCenter.view';

import { SecuredSettingsRoutes } from './components/securedSettingsRoutes';
import { SettingsView } from './settings.view';
import { SettingsAboutView } from './views/about';
import { SettingsAgentsRoutes } from './views/agents';
import { SettingsAIOptionsView } from './views/aiOptions';
import { settingsAIOptionsStyleGuideRoutes } from './views/aiOptionsStyleGuide';
import { SettingsAudioView } from './views/audio';
import { SettingsAudioAnnouncement } from './views/audio/views/announcement';
import { SettingsAudioWaitMusic } from './views/audio/views/waitMusic';
import { settingsAutomationsRoutes } from './views/automations';
import { SettingsBannedUsersView } from './views/bannedUsers';
import { SettingsBillingView } from './views/billing';
import { settingsBusinessHoursRoutes } from './views/businessHours';
import { settingsChatRoutes } from './views/chat';
import { SettingsCopilotTrialView } from './views/copilotTrial/settingsCopilotTrial.view';
import { SettingsCustomAttributesRoutes } from './views/customAttributes/settingsCustomAttributes.routes';
import { settingsCustomFormsRoutes } from './views/customForms';
import { settingsEmailRoutes } from './views/email';
import { SettingsExportConversationsView } from './views/exportConversations/settingsExportConversations.view';
import { settingsIntegrationsRoutes } from './views/integrations';
import { SettingsKeyboardShortcutsView } from './views/keyboardShortcuts';
import { SettingsKnowledgeView } from './views/knowledge';
import { settingsMessagingRoutes } from './views/messaging';
import { settingsMessengerRoutes } from './views/messenger';
import { SettingsOrganizationView } from './views/organization';
import { settingsPhoneRoutes } from './views/phone';
import { preferencesRoutes } from './views/preferences';
import { SettingsProfileView } from './views/profile';
import { settingsQueuesRoutes } from './views/queues';
import { SatisfactionPhoneView } from './views/satisfaction/phone/satisfactionPhone.view';
import { SatisfactionSurveyView } from './views/satisfaction/survey/satisfactionSurvey.view';
import { SettingsScheduledConversationsView } from './views/scheduledConversations';
import { SettingsSmsView } from './views/sms';
import { SettingsTagsView } from './views/tags';
import { settingsTeamsRoutes } from './views/teams';
import { SettingsTemplatesView } from './views/templates';

export const SettingsRoutes = (): JSX.Element => {
  const {
    smsChannel,
    customFields,
    exportConversations,
    helpCenter,
    agentCopilot,
    agentCopilotStyleGuides,
  } = useFeatureFlag([
    'sms-channel',
    'custom-fields',
    'export-conversations',
    'help-center',
    'agent-copilot',
    'agent-copilot-style-guides',
  ]);

  return (
    <SettingsView>
      <Routes>
        <Route index element={<Navigate to="profile" replace />} />
        <Route
          path="scheduledconversations"
          element={<Navigate to="/settings/scheduled-conversations" replace />}
        />
      </Routes>
      <SecuredSettingsRoutes>
        <Route path="profile" element={<SettingsProfileView />} />
        {preferencesRoutes}

        <Route
          path="keyboard-shortcuts"
          element={<SettingsKeyboardShortcutsView />}
        />

        {agentCopilot && (
          <Route path="ai" element={<SettingsAIOptionsView />} />
        )}
        {agentCopilot &&
          agentCopilotStyleGuides &&
          settingsAIOptionsStyleGuideRoutes}
        <Route path="organization" element={<SettingsOrganizationView />} />

        <Route path="agents/*" element={<SettingsAgentsRoutes />} />

        {settingsTeamsRoutes}
        <Route path="billing" element={<SettingsBillingView />} />

        {settingsQueuesRoutes}
        {settingsPhoneRoutes}
        {settingsChatRoutes}
        {settingsEmailRoutes}
        {smsChannel && <Route path="sms" element={<SettingsSmsView />} />}
        {settingsMessagingRoutes}
        {settingsMessengerRoutes}
        {settingsChatbotsRoutes}

        {settingsBusinessHoursRoutes}
        {settingsAutomationsRoutes}
        <Route path="tags" element={<SettingsTagsView />} />
        <Route path="templates" element={<SettingsTemplatesView />} />

        <Route
          path="scheduled-conversations"
          element={<SettingsScheduledConversationsView />}
        />

        {exportConversations && (
          <Route
            path="export-conversations"
            element={<SettingsExportConversationsView />}
          />
        )}
        <Route path="banned-users" element={<SettingsBannedUsersView />} />
        <Route path="knowledge" element={<SettingsKnowledgeView />} />

        {customFields && (
          <Route
            path="custom-attributes/*"
            element={<SettingsCustomAttributesRoutes />}
          />
        )}

        {settingsCustomFormsRoutes}

        <Route path="about" element={<SettingsAboutView />} />

        {settingsIntegrationsRoutes}
        {satisfactionRoutes}
        {audioRoutes}
        {helpCenter && (
          <Route
            path="help-centers/*"
            element={<KnowledgeHelpCenterRoutes />}
          />
        )}

        <Route path="copilot-trial">
          <Route path="" element={<SettingsCopilotTrialView />} />
        </Route>

        <Route
          path="*"
          element={
            <NotFound navigatePath="/settings/profile" name="settings" />
          }
        />
      </SecuredSettingsRoutes>
    </SettingsView>
  );
};

/** The legacy routes to be migrated into views inside new structure. */
const satisfactionRoutes = (
  <Route path="satisfaction" element={<Satisfaction />}>
    <Route index element={<Navigate to="email" replace />} />

    {/* CHAT */}
    <Route path="chat">
      <Route index element={<SatisfactionChatPanel />} />
      <Route path=":language" element={<SatisfactionChatCustomization />} />
    </Route>

    {/* EMAIL */}
    <Route path="email">
      <Route index element={<SatisfactionEmailPanel />} />
      <Route
        path=":language/create"
        element={<SatisfactionEmailCustomization mode="create" />}
      />
      <Route
        path=":language/edit/:settingsId"
        element={<SatisfactionEmailCustomization mode="edit" />}
      />
    </Route>

    {/* MESSENGER */}
    <Route path="messenger">
      <Route index element={<SatisfactionDixaMessengerPanel />} />
      <Route
        path=":language/create"
        element={<DixaMessengerCustomizationPageRoute mode="create" />}
      />
      <Route
        path=":language/edit/:settingsId"
        element={<DixaMessengerCustomizationPageRoute mode="edit" />}
      />
    </Route>

    {/* PHONE */}
    <Route path="phone">
      <Route index element={<SatisfactionPhoneView />} />
      <Route path="survey/:settingsId" element={<SatisfactionSurveyView />} />
      <Route
        path="survey/ivr/:settingsId/:languageCode"
        element={<SurveyCustomizationTextToSpeechView />}
      />
      <Route
        path="survey/sms/:settingsId/:languageCode"
        element={<SurveyCustomizationLinkView />}
      />
    </Route>
  </Route>
);

const audioRoutes = (
  <Route path="audio">
    <Route index element={<SettingsAudioView />} />
    <Route path="wait-music/:id" element={<SettingsAudioWaitMusic />} />
    <Route path="announcement/:id" element={<SettingsAudioAnnouncement />} />
  </Route>
);
